import { Injectable, InjectByToken } from '@jack-henry/frontend-utils/di';
import { exists } from '@jack-henry/frontend-utils/functions';
import {
    DynamicComponentExitReason,
    DynamicComponentHandle,
    DynamicComponentRegistry,
    DynamicComponentResult,
} from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { ObservationSource } from '@jack-henry/frontend-utils/observable';
import { html, ReactiveElement, type TemplateResult } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';
import type { TmSideDrawer } from './side-drawer.component';
import {
    dispatchDrawerResult,
    DRAWER_CONTENT_CONTAINER_ID,
    DrawerConfirmationPromptToken,
    SideDrawerOptions,
    type SideDrawerPromptFn,
} from './side-drawer.service.types';

// eslint-disable-next-line no-duplicate-imports
import './side-drawer.component';

const DEFAULT_OPTIONS = Object.freeze<SideDrawerOptions>({
    host: window.document.body,
    align: 'right',
    buttons: [],
    footerAlignment: 'left',
    promptOnClose: true,
    closeOnBlur: true,
});

type DrawerContent = string | TemplateResult;
/**
 * A service for managing a left- or right-side docked drawer populated with custom content.
 */
@Injectable()
export class TmSideDrawerService {
    constructor(
        private readonly registry: DynamicComponentRegistry<TmSideDrawer>,
        // we have to use an indirect reference via @InjectToken instead of using
        // the dialog service directly since this service is used in legacy code
        @InjectByToken(DrawerConfirmationPromptToken)
        private readonly showConfirmation?: SideDrawerPromptFn
    ) {}

    public get drawerOpen() {
        return exists(this.handle);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private handle?: DynamicComponentHandle<TmSideDrawer, any>;

    private defaultOptions?: Partial<SideDrawerOptions>;

    private readonly contentSource = new ObservationSource<Element>();

    private readonly sideDrawerRef = createRef<TmSideDrawer>();

    /**
     * Set default option values for all side drawers that don't explicitly provide them.
     * Providing `undefined` will clear them.
     */
    public setDefaultOptions(options?: Partial<SideDrawerOptions>) {
        this.defaultOptions = options;
    }

    public async open<T>(
        content: DrawerContent | ((setData: (data: T) => void) => DrawerContent),
        title: string,
        options: Partial<SideDrawerOptions> = {}
    ) {
        const allOptions = this.normalizeOptions(options);
        if (this.drawerOpen) {
            const result = await this.handleOpenDrawer(allOptions.promptOnClose, title);
            // if we have a result, it means the open was aborted so we should return
            // the original handle's disposed promise; otherwise, continue to open the new drawer
            if (result) {
                return result;
            }
        }

        content =
            typeof content === 'function' ? content(data => this.dispatchData(data)) : content;
        const handle = this.registry.create<T>(() => {
            return this.renderDrawer(title, content, allOptions);
        }, allOptions);

        this.handle = handle;

        return handle.disposed;
    }

    public async close<T>(reason = DynamicComponentExitReason.ForceClose, data?: T) {
        if (!this.handle) {
            return;
        }

        const { handle } = this;
        this.handle = undefined;
        handle.dispose({
            reason,
            data,
        });
        await handle.disposed;
    }

    public async getDrawer() {
        if (!this.handle) {
            throw new Error('Could not get side drawer element. No drawer is open.');
        }

        const { element } = this.handle;
        await element.updateComplete;
        return element;
    }

    public getContent<E extends Element = Element>() {
        return this.contentSource.toPromise(true) as Promise<E>;
    }

    private async handleOpenDrawer(promptOnClose: boolean, title: string) {
        if (!this.handle) {
            throw new Error('No drawer is currently open');
        }

        // assign to a local variable in case it becomes cleared while we're awaiting
        const { handle } = this;
        if (promptOnClose) {
            if (!exists(this.showConfirmation)) {
                throw new Error(
                    'Could not show drawer confirmation prompt. It was not provided via injection token.'
                );
            }

            const { heading } = await this.getDrawer();
            const prompt = `You're currently working in ${heading} and are attempting to open ${title}. Are you sure you want to close it and abandon your work?`;
            const content = html`<p style="width: 300px">${prompt}</p>`;
            const { reason } = await this.showConfirmation(content, `Close ${heading}?`);

            // give the handle of the currently open drawer on aborting
            // so consumers can still wait for it to close
            if (reason !== DynamicComponentExitReason.Confirm) {
                // should we throw here to allow consumers to handle the abort case?
                return handle.disposed;
            }
        }

        return this.close();
    }

    private async contentChanged(content?: Element) {
        if (!content) {
            return;
        }

        if (this.sideDrawerRef.value) {
            // firstElementChild won't be ready until after the update cycle
            await this.sideDrawerRef.value.updateComplete;
        }

        const { firstElementChild } = content;
        if (!firstElementChild) {
            throw new Error('Could not determine content element');
        }

        if (firstElementChild instanceof ReactiveElement) {
            await firstElementChild.updateComplete;
        }

        // content is the outer element;
        // the first child is the provided element
        this.contentSource.emit(firstElementChild);
    }

    /**
     * Dispatch from the active content element.
     * Intended to be passed to render hook.
     */
    private dispatchData<T>(data: T) {
        this.getContent().then(contentElement => dispatchDrawerResult(contentElement, data));
    }

    private normalizeOptions(options: Partial<SideDrawerOptions>): SideDrawerOptions {
        return {
            ...DEFAULT_OPTIONS,
            ...this.defaultOptions,
            ...options,
        };
    }

    private renderDrawer<T>(
        title: string,
        content: string | TemplateResult,
        options: SideDrawerOptions
    ) {
        const { align, buttons, footerAlignment, closeOnBlur } = options;
        const alignment = align === 'left' ? 'left: 0' : 'right: 0';
        const style = `position: absolute; ${alignment}; top: 0; z-index: 1050;`;
        const contentTemplate = typeof content === 'string' ? html`<p>${content}</p>` : content;

        return html`<tm-side-drawer
            ${ref(this.sideDrawerRef)}
            style=${style}
            .alignment=${align}
            .heading=${title}
            .buttons=${buttons}
            .footerAlignment=${footerAlignment}
            .closeOnBlur=${closeOnBlur}
            @close-drawer=${(e: CustomEvent<DynamicComponentResult<T>>) => {
                // don't let the event bubble out
                e.preventDefault();
                const { reason, data } = e.detail || {};
                this.close(reason, data);
            }}
        >
            <div
                id="${DRAWER_CONTENT_CONTAINER_ID}"
                ${ref(content => this.contentChanged(content))}
            >
                ${contentTemplate}
            </div>
        </tm-side-drawer>`;
    }
}
